import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { Visibility, MoreHoriz, FilterAlt, Clear } from '@mui/icons-material';
import CardComponent from '@jumbo/components/common/Card';
import JumboDdMenu from '@jumbo/components/JumboDdMenu/JumboDdMenu';
import CustomTable from '@jumbo/components/CustomeTable';
import { createOrderRoute } from '@jumbo/utils/constants/routes';
import { getOrderList, getStoreFolders } from 'app/redux/slice/orderSlice';
import Loader from '@jumbo/components/common/Loader';
import {
  Grid,
  TextField,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  MenuItem,
  Stack,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LoadingButton } from '@mui/lab';

const INITIAL_FILTERS = {
  source_id: '',
  email: '',
  customer_first_name: '',
  customer_last_name: '',
  shipping_first_name: '',
  shipping_last_name: '',
  customer_phone: '',
  shipping_phone: '',
  customer_company: '',
  shipping_company: '',
  search_start_date: '',
  search_end_date: '',
  folder_id: '',
  order_by: 'date_added',
  order: 'DESC',
};

const SORT_BY_OPTIONS = [
  { value: 'date_added', label: 'Date Added' },
  { value: 'source_id', label: 'Order ID' },
  { value: 'date_updated', label: 'Date Updated' },
];

const SORT_ORDER_OPTIONS = [
  { value: 'DESC', label: 'Descending' },
  { value: 'ASC', label: 'Ascending' },
];

const Order = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const storeId = localStorage.getItem('selectedStoreId');
  const orderData = useSelector((state) => state?.order);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(Number(process.env.REACT_APP_PER_PAGE ?? 10));
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ ...INITIAL_FILTERS });
  const [appliedFilters, setAppliedFilters] = useState({ ...INITIAL_FILTERS });
  const [expanded, setExpanded] = useState(false);

  // Fetch store folders on mount
  useEffect(() => {
    if (storeId) {
      dispatch(getStoreFolders({ selectedStoreId: storeId }));
    }
  }, [dispatch, storeId]);

  const handleItemAction = (menuItem, record) => {
    switch (menuItem.action) {
      case 'view':
        navigate(createOrderRoute(record?.id));
        break;
      default:
        break;
    }
  };

  const handleFilterChange = useCallback(
    (field) => (evt) => {
      setFilters((prev) => ({ ...prev, [field]: evt.target.value }));
    },
    [],
  );

  const handleApplyFilters = () => {
    setPage(0);
    setAppliedFilters({ ...filters });
  };

  const handleClearFilters = () => {
    setPage(0);
    setFilters({ ...INITIAL_FILTERS });
    setAppliedFilters({ ...INITIAL_FILTERS });
  };

  // Check if any filter (beyond sort defaults) is active
  const hasActiveFilters = Object.keys(INITIAL_FILTERS).some((key) => {
    if (key === 'order_by' || key === 'order') return false;
    return appliedFilters[key] !== '' && appliedFilters[key] !== INITIAL_FILTERS[key];
  });

  const columns = [
    {
      label: 'Order Id',
      key: 'source_id',
      sort: true,
    },
    {
      label: 'Order Name',
      render: (elm) => <>{elm?.order_items?.map((i) => i?.name).join(' || ')}</>,
    },
    {
      label: 'Tracking Number',
      render: (elm) => (
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {elm?.order_shipments?.length
            ? elm.order_shipments.map((s, idx) => (
                <Link
                  key={idx}
                  to={s.tracking_url}
                  target="_blank"
                  style={{
                    padding: '4px 8px',
                    background: '#e3f2fd',
                    borderRadius: '6px',
                    fontSize: '12px',
                    textDecoration: 'none',
                    color: '#1976d2',
                  }}>
                  {s.tracking_number}
                </Link>
              ))
            : '-'}
        </div>
      ),
    },
    {
      label: 'Status',
      key: 'orderStatus',
      sort: true,
    },
    {
      label: 'Customer Name',
      render: (elm) => <> {`${elm?.customer?.first_name} ${elm?.customer?.last_name}`}</>,
    },
    {
      label: 'Shipping Name',
      render: (elm) => <> {`${elm?.shipping?.first_name} ${elm?.shipping?.last_name}`}</>,
    },
    {
      label: 'Action',
      render: (elm) => {
        const menuItems = [{ icon: <Visibility />, title: 'View', action: 'view' }];
        return (
          <JumboDdMenu
            icon={<MoreHoriz />}
            menuItems={menuItems}
            onClickCallback={(event) => handleItemAction(event, elm)}
          />
        );
      },
    },
  ];

  useEffect(() => {
    const fetchOrders = async () => {
      if (storeId) {
        setLoading(true);
        const params = {
          offset: page * rowsPerPage,
          limit: rowsPerPage,
          selectedStoreId: storeId,
        };
        // Add only non-empty filter values
        Object.entries(appliedFilters).forEach(([key, value]) => {
          if (value !== '' && value !== undefined) {
            params[key] = value;
          }
        });
        await dispatch(getOrderList(params));
        setLoading(false);
      }
    };
    fetchOrders();
  }, [page, rowsPerPage, dispatch, appliedFilters, storeId]);

  return (
    <>
      {/* Advanced Filters Accordion */}
      <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)} sx={{ mb: 3.5 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <FilterAlt color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Advanced Filters
            </Typography>
            {hasActiveFilters && (
              <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
                (Active)
              </Typography>
            )}
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            {/* Row 1: Order ID, Email, Folder */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Order ID"
                value={filters.source_id}
                onChange={handleFilterChange('source_id')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Email" value={filters.email} onChange={handleFilterChange('email')} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                select
                label="Folder"
                value={filters.folder_id}
                onChange={handleFilterChange('folder_id')}>
                <MenuItem value="">All Folders</MenuItem>
                {(orderData?.folders || []).map((folder) => (
                  <MenuItem key={folder.id} value={folder.id}>
                    {folder.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Row 2: Customer Names, Shipping Names */}
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Customer First Name"
                value={filters.customer_first_name}
                onChange={handleFilterChange('customer_first_name')}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Customer Last Name"
                value={filters.customer_last_name}
                onChange={handleFilterChange('customer_last_name')}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Shipping First Name"
                value={filters.shipping_first_name}
                onChange={handleFilterChange('shipping_first_name')}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Shipping Last Name"
                value={filters.shipping_last_name}
                onChange={handleFilterChange('shipping_last_name')}
              />
            </Grid>

            {/* Row 3: Phones, Companies */}
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Customer Phone"
                value={filters.customer_phone}
                onChange={handleFilterChange('customer_phone')}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Shipping Phone"
                value={filters.shipping_phone}
                onChange={handleFilterChange('shipping_phone')}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Customer Company"
                value={filters.customer_company}
                onChange={handleFilterChange('customer_company')}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Shipping Company"
                value={filters.shipping_company}
                onChange={handleFilterChange('shipping_company')}
              />
            </Grid>

            {/* Row 4: Date Range, Sort */}
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Start Date"
                type="date"
                value={filters.search_start_date}
                onChange={handleFilterChange('search_start_date')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="End Date"
                type="date"
                value={filters.search_end_date}
                onChange={handleFilterChange('search_end_date')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                select
                label="Sort By"
                value={filters.order_by}
                onChange={handleFilterChange('order_by')}>
                {SORT_BY_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                select
                label="Sort Order"
                value={filters.order}
                onChange={handleFilterChange('order')}>
                {SORT_ORDER_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button variant="outlined" color="secondary" startIcon={<Clear />} onClick={handleClearFilters}>
                  Clear Filters
                </Button>
                <LoadingButton loading={loading} variant="contained" startIcon={<FilterAlt />} onClick={handleApplyFilters}>
                  Apply Filters
                </LoadingButton>
              </Stack>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Orders Table */}
      {orderData.loading ? (
        <Loader />
      ) : (
        <CardComponent title="List of Orders">
          <CustomTable
            columns={columns}
            record={orderData?.list || []}
            setPage={setPage}
            page={page}
            setRowsPerPage={setRowsPerPage}
            rowsPerPage={rowsPerPage}
            total={orderData?.total}
          />
        </CardComponent>
      )}
    </>
  );
};

export default Order;
